import React, { forwardRef, useRef, useState } from 'react';
import {
  DayKeydownPayload,
  MonthSettings,
} from '~/components/shared/DatePicker/Month';
import MonthsList from '~/components/shared/DatePicker/MonthsList';
import useUncontrolled from '~/hooks/useUncontrolled';
import MonthPicker from '~/components/shared/DatePicker/MonthPicker';
import YearPicker from '~/components/shared/DatePicker/YearPicker';

export interface CalendarProps extends MonthSettings {
  month?: Date;
  initialMonth?: Date;

  onMonthChange?(month: Date): void;

  locale?: string;
  amountOfMonths?: number;
  paginateBy?: number;
  value?: Date;
  allowLevelChange?: boolean;
  initialLevel?: 'date' | 'month' | 'year';

  renderDay?(date: Date): React.ReactNode;

  onChange?(value: Date): void;

  onDayMouseEnter?(date: Date, event: React.MouseEvent): void;

  nextMonthLabel?: string;
  previousMonthLabel?: string;
  nextYearLabel?: string;
  previousYearLabel?: string;
  nextDecadeLabel?: string;
  previousDecadeLabel?: string;
  labelFormat?: string;
  yearLabelFormat?: string;
  weekdayLabelFormat?: string;
}

const Calendar = forwardRef<HTMLDivElement, CalendarProps>(
  (
    {
      month,
      initialMonth,
      onMonthChange,
      locale,
      amountOfMonths = 1,
      paginateBy = amountOfMonths,
      allowLevelChange = true,
      initialLevel = 'date',
      minDate,
      maxDate,
      dayClassName,
      dayStyle,
      disableOutsideEvents,
      excludeDate,
      hideWeekdays,
      fullWidth,
      preventFocus,
      firstDayOfWeek = 'monday',
      value,
      onChange,
      onDayMouseEnter,
      nextDecadeLabel,
      nextMonthLabel,
      nextYearLabel,
      previousDecadeLabel,
      previousMonthLabel,
      previousYearLabel,
      labelFormat = 'MMMM YYYY',
      weekdayLabelFormat,
      hideOutsideDates,
      isDateInRange,
      isDateFirstInRange,
      isDateLastInRange,
      renderDay,
      weekendDays,
      yearLabelFormat = 'YYYY',
    },
    ref
  ) => {
    const [selectionState, setSelectionState] = useState(initialLevel);
    const finalLocale = locale || 'en';
    const minYear = minDate instanceof Date ? minDate.getFullYear() : 100;
    const maxYear = maxDate instanceof Date ? maxDate.getFullYear() : 10000;

    const daysRefs = useRef<HTMLButtonElement[][][]>(
      Array(amountOfMonths)
        .fill(0)
        .map(() => [])
    );

    const [_month, setMonth] = useUncontrolled({
      value: month,
      defaultValue: initialMonth,
      finalValue: new Date(),
      onChange: onMonthChange,
    });

    const [yearSelection, setYearSelection] = useState(_month.getFullYear());

    const focusOnNextFocusableDay = (
      direction: 'down' | 'up' | 'left' | 'right',
      monthIndex: number,
      payload: DayKeydownPayload,
      n = 1
    ) => {
      const changeRow = ['down', 'up'].includes(direction);

      const rowIndex = changeRow
        ? payload.rowIndex + (direction === 'down' ? n : -n)
        : payload.rowIndex;

      const cellIndex = changeRow
        ? payload.cellIndex
        : payload.cellIndex + (direction === 'right' ? n : -n);

      const dayToFocus = daysRefs.current[monthIndex][rowIndex][cellIndex];

      if (!dayToFocus) {
        return;
      }

      if (dayToFocus.disabled) {
        // Day is disabled, call this function recursively until
        // we find a non-disabled day or there are no more days
        focusOnNextFocusableDay(direction, monthIndex, payload, n + 1);
      } else {
        dayToFocus.focus();
      }
    };

    const daysPerRow = 6;

    const handleDayKeyDown = (
      monthIndex: number,
      payload: DayKeydownPayload,
      event: React.KeyboardEvent<HTMLButtonElement>
    ) => {
      event.preventDefault();

      switch (event.key) {
        case 'ArrowDown': {
          const hasRowBelow =
            payload.rowIndex + 1 < daysRefs.current[monthIndex].length;
          if (hasRowBelow) {
            focusOnNextFocusableDay('down', monthIndex, payload);
          }
          break;
        }

        case 'ArrowUp': {
          const hasRowAbove = payload.rowIndex > 0;
          if (hasRowAbove) {
            focusOnNextFocusableDay('up', monthIndex, payload);
          }
          break;
        }

        case 'ArrowRight': {
          const isNotLastCell = payload.cellIndex !== daysPerRow;
          if (isNotLastCell) {
            focusOnNextFocusableDay('right', monthIndex, payload);
          } else if (monthIndex + 1 < amountOfMonths) {
            if (daysRefs.current[monthIndex + 1][payload.rowIndex]) {
              daysRefs.current[monthIndex + 1][payload.rowIndex][0]?.focus();
            }
          }

          break;
        }

        case 'ArrowLeft': {
          if (payload.cellIndex !== 0) {
            focusOnNextFocusableDay('left', monthIndex, payload);
          } else if (monthIndex > 0) {
            if (daysRefs.current[monthIndex - 1][payload.rowIndex]) {
              daysRefs.current[monthIndex - 1][payload.rowIndex][
                daysPerRow
              ].focus();
            }
          }
          break;
        }
        default:
          break;
      }
    };

    return (
      <div ref={ref}>
        {selectionState === 'year' && (
          <YearPicker
            value={yearSelection}
            minYear={minYear}
            maxYear={maxYear}
            onChange={(year) => {
              setYearSelection(year);
              setSelectionState('month');
            }}
            nextDecadeLabel={nextDecadeLabel}
            previousDecadeLabel={previousDecadeLabel}
            preventFocus={preventFocus}
            yearLabelFormat={yearLabelFormat}
          />
        )}
        {selectionState === 'month' && (
          <MonthPicker
            value={{ month: _month.getMonth(), year: _month.getFullYear() }}
            year={yearSelection}
            onYearChange={setYearSelection}
            onNextLevel={() => setSelectionState('year')}
            locale={finalLocale}
            minDate={minDate}
            maxDate={maxDate}
            onChange={(monthValue) => {
              setMonth(new Date(yearSelection, monthValue, 1));
              setSelectionState('date');
            }}
            nextYearLabel={nextYearLabel}
            previousYearLabel={previousYearLabel}
            preventFocus={preventFocus}
            yearLabelFormat={yearLabelFormat}
          />
        )}
        {selectionState === 'date' && (
          <MonthsList
            amountOfMonths={amountOfMonths}
            paginateBy={paginateBy}
            month={_month}
            locale={finalLocale}
            minDate={minDate}
            maxDate={maxDate}
            allowLevelChange={allowLevelChange}
            daysRefs={daysRefs}
            onMonthChange={setMonth}
            onNextLevel={() => setSelectionState('month')}
            onDayKeyDown={handleDayKeyDown}
            dayClassName={dayClassName}
            dayStyle={dayStyle}
            disableOutsideEvents={disableOutsideEvents}
            excludeDate={excludeDate}
            hideWeekdays={hideWeekdays}
            fullWidth={fullWidth}
            preventFocus={preventFocus}
            firstDayOfWeek={firstDayOfWeek}
            value={value}
            onChange={onChange}
            nextMonthLabel={nextMonthLabel}
            previousMonthLabel={previousMonthLabel}
            labelFormat={labelFormat}
            weekdayLabelFormat={weekdayLabelFormat}
            onDayMouseEnter={onDayMouseEnter}
            renderDay={renderDay}
            hideOutsideDates={hideOutsideDates}
            isDateInRange={isDateInRange}
            isDateFirstInRange={isDateFirstInRange}
            isDateLastInRange={isDateLastInRange}
            weekendDays={weekendDays}
          />
        )}
      </div>
    );
  }
);

export default Calendar;
